// Pure helpers for the favorites/saved logic. Kept framework-free so they
// can be unit-tested without React or a DOM.

export const FAVORITES_KEY = "what.favorites";
export const FAVORITES_EVENT = "what.favorites.changed";

// Legacy → canonical id remap for places whose id changed over time.
export const FAVORITE_ALIASES: Record<string, string> = {
  "do-suomenlinna": "suomenlinna",
  "suomenlinna-fortress": "suomenlinna",
  "suomenlinna-sea-fortress": "suomenlinna",
};

/** Normalize a raw favorites array: remap legacy ids, drop falsy, dedupe. */
export function normalizeFavorites(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const mapped = raw
    .filter((x): x is string => typeof x === "string" && x.length > 0)
    .map((id) => FAVORITE_ALIASES[id] ?? id);
  return Array.from(new Set(mapped));
}

export type SavedItemLike = { id: string; name: string };

/**
 * Given a favorites list and the full catalog, return the unique items to
 * show on the Saved page — deduped by both id and case-insensitive name so
 * that two catalog entries pointing at the same real-world place can never
 * both surface.
 */
export function selectSavedItems<T extends SavedItemLike>(
  favorites: string[],
  catalog: readonly T[],
): T[] {
  const favSet = new Set(normalizeFavorites(favorites));
  const seenIds = new Set<string>();
  const seenNames = new Set<string>();
  const out: T[] = [];
  for (const item of catalog) {
    if (!favSet.has(item.id)) continue;
    const nameKey = item.name.trim().toLowerCase();
    if (seenIds.has(item.id) || seenNames.has(nameKey)) continue;
    seenIds.add(item.id);
    seenNames.add(nameKey);
    out.push(item);
  }
  return out;
}
